import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useApp, apiFetch } from "../../context/AppContext";
import LiveTourOverlay from "../help/LiveTourOverlay";
import {
  LayoutDashboard,
  Home,
  Users,
  Building2,
  ShieldCheck,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Monitor,
  MonitorOff,
  Volume2,
  VolumeX,
  Package,
  ClipboardList,
  FileText,
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  SearchCheck,
  Network,
  Eye,
  BookOpen,
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  ScrollText,
  Crown,
  Globe,
} from "lucide-react";

import {
  getNotificationCategoryMeta,
  parseNotificationMessage,
  showDesktopNotification,
} from "../../utils/notificationDisplay";
import { isNotificationSoundEnabled } from "../../utils/notificationSound";

/* ── Icon colors per nav label ─────────────────────────────────────────────── */
const iconColors = {
  Home: "text-sky-400",
  Dashboard: "text-emerald-400",
  "Global Compliance": "text-teal-300",
  Assessment: "text-cyan-400",
  "Gap Assessment": "text-cyan-400",
  "PIA Tracking": "text-amber-400",
  "TPRM Tracking": "text-blue-400",
  PIA: "text-amber-400",
  ROPA: "text-violet-400",
  OneDPDP: "text-blue-400",
  TPRM: "text-blue-400",
  Operations: "text-rose-400",
  "Breach Management": "text-red-400",
  "Consent Variance": "text-violet-400",
  DSAR: "text-blue-400",
  Resources: "text-indigo-300",
  "Asset Tracker": "text-teal-400",
  "Policy Store": "text-indigo-400",
  Reports: "text-fuchsia-400",
  Help: "text-sky-300",
  "Tenant Admins": "text-orange-400",
  Departments: "text-teal-400",
  "Head Department": "text-amber-400",
  Questionnaire: "text-cyan-400",
  "User Responses": "text-indigo-400",
  Licenses: "text-violet-400",
  "Audit Logs": "text-amber-300",
  "Compliance Checklists": "text-emerald-400",
  Settings: "text-slate-300",
};

const NAV_TOUR_IDS = {
  Home: "nav-home",
  Dashboard: "nav-dashboard",
  "Global Compliance": "nav-global-compliance",
  Assessment: "nav-assessment",
  Operations: "nav-operations",
  "Breach Management": "nav-breach-management",
  "Consent Variance": "nav-consent-variance",
  DSAR: "nav-dsar",
  Resources: "nav-resources",
  "Asset Tracker": "nav-asset-tracker",
  "Policy Store": "nav-policy-store",
  Reports: "nav-reports",
  Help: "nav-help",
  "Tenant Admins": "nav-tenant-admins",
  Licenses: "nav-licenses",
  Settings: "nav-settings",
  Users: "nav-users",
  Departments: "nav-departments",
  "Gap Assessment": "nav-gap-assessment",
  "PIA Tracking": "nav-pia-tracking",
  "TPRM Tracking": "nav-tprm-tracking",
  "User Responses": "nav-form-responses",
  "Form Questions": "nav-form-questions",
  License: "nav-license",
  "Audit Logs": "nav-audit-logs",
};

const NAV_MODULE_MAP = {
  "Gap Assessment": "gap",
  "PIA Tracking": "dpia",
  "TPRM Tracking": "tprm",
  ROPA: "ropa",
  PIA: "dpia",
  TPRM: "tprm",
  DSAR: "dsar",
  "Consent Variance": "consent",
  "Breach Management": "breach",
  "Policy Store": "policy_store",
  Reports: "reports",
  "Asset Tracker": "asset_tracker",
  // "User Responses" / "Form Questions" inherit whatever assessment type they're viewing —
  // gate those inside the page itself, or duplicate them per-module if they're module-specific.
};

function filterByModules(items, allowedModules) {
  return items.filter((item) => {
    const requiredModule = NAV_MODULE_MAP[item.label];
    return !requiredModule || (allowedModules || []).includes(requiredModule);
  });
}

function navTourId(label) {
  return NAV_TOUR_IDS[label];
}

const ADMIN_NAV = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: Building2, label: "Tenant Admins", path: "/admin/tenants" },
  { icon: ShieldCheck, label: "Licenses", path: "/admin/licenses" },
  {
    icon: ClipboardCheck,
    label: "Compliance Checklists",
    path: "/admin/compliance-checklists",
  },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
  { icon: CircleHelp, label: "Help", path: "/help" },
];

const TENANT_ADMIN_MENU_GROUPS = [
  {
    label: "People & setup",
    icon: Users,
    hint: "Accounts, teams, and setup",
    items: [
      {
        icon: Users,
        label: "Users",
        path: "/tenant/users",
        hint: "Accounts and access",
      },
      {
        icon: Network,
        label: "Departments",
        path: "/tenant/departments",
        hint: "Teams and ownership",
      },
      {
        icon: FileText,
        label: "Form Questions",
        path: "/tenant/form-questions/gap_assessment",
        hint: "Assessment questions",
      },
      {
        icon: ClipboardCheck,
        label: "Compliance Checklists",
        path: "/tenant/compliance-checklists",
        hint: "Country checklists",
      },
    ],
  },
  {
    label: "Oversight",
    icon: ClipboardCheck,
    hint: "Reviews, scores, and responses",
    items: [
      {
        icon: SearchCheck,
        label: "Gap Assessment",
        path: "/tenant/gap-assessment",
        hint: "Progress and scores",
      },
      {
        icon: ClipboardList,
        label: "PIA Tracking",
        path: "/tenant/pia-tracking",
        hint: "Privacy impact reviews",
      },
      {
        icon: Building2,
        label: "TPRM Tracking",
        path: "/tenant/tprm-tracking",
        hint: "Vendor risk reviews",
      },
      {
        icon: Eye,
        label: "User Responses",
        path: "/tenant/form-responses",
        hint: "Submitted answers",
      },
    ],
  },
];

const TENANT_ADMIN_DROPDOWN_ITEMS = TENANT_ADMIN_MENU_GROUPS.flatMap(
  (group) => group.items,
);

const TENANT_NAV = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/tenant" },
  { icon: Package, label: "License", path: "/tenant/license" },
  { icon: ScrollText, label: "Audit Logs", path: "/tenant/audit-logs" },
  { icon: Settings, label: "Settings", path: "/tenant/settings" },
  { icon: CircleHelp, label: "Help", path: "/help" },
];

const USER_ASSESSMENT_ITEMS = [
  {
    icon: SearchCheck,
    label: "Gap Assessment",
    path: "/user/gap-assessment",
  },
  {
    icon: FileText,
    label: "ROPA",
    path: "/user/ropa",
  },
  {
    icon: ClipboardList,
    label: "PIA",
    path: "/user/pia",
  },
  {
    icon: Building2,
    label: "TPRM",
    path: "/user/tprm",
  },
];

const USER_OPERATIONS_ITEMS = [
  {
    icon: AlertTriangle,
    label: "Breach Management",
    path: "/user/breach-management",
  },
  {
    icon: ArrowLeftRight,
    label: "Consent Variance",
    path: "/user/consent-variance",
  },
  {
    icon: ShieldCheck,
    label: "DSAR",
    path: "/user/dsar",
  },
];

const USER_RESOURCES_ITEMS = [
  {
    icon: Network,
    label: "Asset Tracker",
    path: "/user/asset-tracker",
  },
  {
    icon: BookOpen,
    label: "Policy Store",
    path: "/user/policy-store",
  },
  {
    icon: BarChart3,
    label: "Reports",
    path: "/user/reports",
  },
  {
    icon: CircleHelp,
    label: "Help",
    path: "/help",
  },
];

const USER_NAV = [
  {
    icon: Home,
    label: "Home",
    path: "/user",
  },
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/user/dashboard",
  },
  {
    icon: Globe,
    label: "Global Compliance",
    path: "/user/global-compliance",
  },
];

/* ── Helper: get display name from real API user object ─────────────────────
   Backend returns full_name for all roles, but we keep fallbacks just in case. */
function getDisplayName(user) {
  return user?.full_name || user?.name || user?.email || "?";
}

function getDepartmentLabel(user) {
  const names = Array.isArray(user?.department_names)
    ? user.department_names.filter(Boolean)
    : [];
  const base = names.length ? names.join(", ") : user?.department_name || "";
  if (user?.head_access && user?.role === "user") {
    return base ? `${base} · all departments` : "All departments";
  }
  return base;
}

function isNavActive(path, pathname) {
  if (path === "/user" || path === "/tenant" || path === "/admin")
    return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

function isAssessmentActive(pathname) {
  return USER_ASSESSMENT_ITEMS.some((item) => isNavActive(item.path, pathname));
}

function isOperationsActive(pathname) {
  return USER_OPERATIONS_ITEMS.some((item) => isNavActive(item.path, pathname));
}

function isResourcesActive(pathname) {
  return USER_RESOURCES_ITEMS.some((item) => isNavActive(item.path, pathname));
}

function isTenantAdminDropdownActive(pathname) {
  return TENANT_ADMIN_DROPDOWN_ITEMS.some((item) =>
    isNavActive(item.path, pathname),
  );
}

function TopNavLink({ item, active, navLinkClass, iconColors }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      data-tour-id={navTourId(item.label)}
      className={`${navLinkClass(active, true)} flex-shrink-0`}
      title={item.label}
    >
      {active && (
        <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      )}
      <Icon
        size={13}
        className={`flex-shrink-0 transition-all ${
          active
            ? "text-white"
            : `${iconColors[item.label]} group-hover:brightness-125`
        }`}
      />
      <span>{item.label}</span>
    </Link>
  );
}

function NavMenuLink({ item, pathname, onClick, detailed = false }) {
  const ItemIcon = item.icon;
  const itemActive = isNavActive(item.path, pathname);
  const iconClass = itemActive
    ? "text-indigo-600"
    : iconColors[item.label] || "text-slate-500";

  if (!detailed) {
    return (
      <Link
        to={item.path}
        role="menuitem"
        data-tour-id={navTourId(item.label)}
        onClick={onClick}
        className={`flex items-center gap-2.5 px-3 py-2.5 mx-1.5 rounded-lg text-sm font-semibold transition-all ${
          itemActive
            ? "bg-indigo-50 text-indigo-900"
            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
        }`}
      >
        <ItemIcon size={15} className={iconClass} />
        <span className="text-inherit">{item.label}</span>
      </Link>
    );
  }

  return (
    <Link
      to={item.path}
      role="menuitem"
      data-tour-id={navTourId(item.label)}
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${
        itemActive ? "bg-indigo-50" : "hover:bg-slate-50"
      }`}
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ${
          itemActive
            ? "bg-white text-indigo-600 ring-indigo-100"
            : "bg-slate-50 ring-slate-100"
        }`}
      >
        <ItemIcon size={13} className={iconClass} />
      </span>
      <span className="min-w-0">
        <span
          className={`block truncate text-[13px] font-semibold leading-tight ${
            itemActive ? "text-indigo-900" : "text-slate-800"
          }`}
        >
          {item.label}
        </span>
        {item.hint && (
          <span className="mt-0.5 block truncate text-[11px] font-normal leading-tight text-slate-500">
            {item.hint}
          </span>
        )}
      </span>
    </Link>
  );
}

function NavDropdown({
  label,
  icon: Icon,
  items,
  groups,
  pathname,
  navLinkClass,
  iconColor,
  onNavigate,
  isActiveCheck,
}) {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, minWidth: 220 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const active = isActiveCheck
    ? isActiveCheck(pathname)
    : isAssessmentActive(pathname);
  const selectedGroup = groups?.find((group) => group.label === openGroup);

  const updateMenuPosition = (groupLabel = openGroup) => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const grouped = Boolean(groups?.length);
    const width = grouped
      ? groupLabel
        ? 448
        : 212
      : Math.max(rect.width, 220);
    const maxLeft = window.innerWidth - width - 12;
    setMenuPos({
      top: rect.bottom + 6,
      left: Math.max(12, Math.min(rect.left, maxLeft)),
      minWidth: width,
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const onPointerDown = (event) => {
      const target = event.target;
      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };

    const onReposition = () => updateMenuPosition();
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onPointerDown);
    }, 0);

    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, openGroup]);

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
  }, [pathname]);

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          top: menuPos.top,
          left: menuPos.left,
          minWidth: menuPos.minWidth,
          zIndex: 9999,
        }}
        className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.16)] ring-1 ring-slate-900/5"
        role="menu"
      >
        {groups?.length ? (
          <div className="flex items-stretch">
            <div className="w-[212px] shrink-0 bg-slate-50 py-2">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                Sections
              </p>
              <div className="space-y-0.5 px-1.5">
                {groups.map((group) => {
                  const GroupIcon = group.icon;
                  const selected = openGroup === group.label;
                  const containsActive = group.items.some((item) =>
                    isNavActive(item.path, pathname),
                  );
                  return (
                    <button
                      key={group.label}
                      type="button"
                      role="menuitem"
                      aria-expanded={selected}
                      onMouseEnter={() => setOpenGroup(group.label)}
                      onFocus={() => setOpenGroup(group.label)}
                      onClick={() => setOpenGroup(group.label)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-semibold transition-colors ${
                        selected
                          ? "bg-white text-indigo-900 shadow-sm ring-1 ring-slate-200"
                          : containsActive
                            ? "text-indigo-900 hover:bg-white"
                            : "text-slate-700 hover:bg-white"
                      }`}
                    >
                      {GroupIcon && (
                        <GroupIcon
                          size={15}
                          className={
                            selected || containsActive
                              ? "text-indigo-600"
                              : "text-slate-500"
                          }
                        />
                      )}
                      <span className="min-w-0 flex-1 truncate">
                        {group.label}
                      </span>
                      <ChevronRight
                        size={14}
                        className={
                          selected ? "text-indigo-500" : "text-slate-400"
                        }
                      />
                    </button>
                  );
                })}
              </div>
            </div>
            {selectedGroup && (
              <div className="w-[236px] shrink-0 border-l border-slate-200 bg-white py-2">
                <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400">
                  {selectedGroup.label}
                </p>
                {selectedGroup.items.map((item) => (
                  <NavMenuLink
                    key={item.path}
                    item={item}
                    pathname={pathname}
                    onClick={() => {
                      setOpen(false);
                      setOpenGroup(null);
                      onNavigate?.();
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="py-1.5">
            {items.map((item) => (
              <NavMenuLink
                key={item.path}
                item={item}
                pathname={pathname}
                onClick={() => {
                  setOpen(false);
                  onNavigate?.();
                }}
              />
            ))}
          </div>
        )}
      </div>,
      document.body,
    );

  return (
    <div className="relative flex-shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!open) {
            const current = groups?.find((group) =>
              group.items.some((item) => isNavActive(item.path, pathname)),
            );
            const nextGroup = current?.label || null;
            setOpenGroup(nextGroup);
            updateMenuPosition(nextGroup);
          }
          setOpen((prev) => !prev);
        }}
        data-tour-id={
          label === "Assessment"
            ? "nav-assessment"
            : label === "Operations"
              ? "nav-operations"
              : label === "Resources"
                ? "nav-resources"
                : label === "Tenant Admins"
                  ? "nav-tenant-admins"
                  : undefined
        }
        className={navLinkClass(active || open, true)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {(active || open) && (
          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        )}
        <Icon
          size={13}
          className={`flex-shrink-0 transition-all ${
            active || open
              ? "text-white"
              : `${iconColor} group-hover:brightness-125`
          }`}
        />
        <span>{label}</span>
        <ChevronDown
          size={12}
          className={`flex-shrink-0 opacity-70 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {menu}
    </div>
  );
}

function MobileNavGroup({
  label,
  icon: Icon,
  iconColor,
  tourId,
  pathname,
  navLinkClass,
  onNavigate,
  defaultExpanded = false,
  items,
  isActiveCheck,
}) {
  const active = isActiveCheck(pathname);
  const [expanded, setExpanded] = useState(defaultExpanded || active);

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        data-tour-id={tourId}
        className={`${navLinkClass(active)} w-full`}
        aria-expanded={expanded}
      >
        {active && (
          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        )}
        <Icon size={16} className={active ? "text-white" : iconColor} />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          size={14}
          className={`opacity-70 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="ml-2 pl-2 border-l border-white/10 space-y-1">
          {items.map((item) => {
            const ItemIcon = item.icon;
            const itemActive = isNavActive(item.path, pathname);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                data-tour-id={navTourId(item.label)}
                className={navLinkClass(itemActive)}
              >
                {itemActive && (
                  <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                )}
                <ItemIcon
                  size={15}
                  className={itemActive ? "text-white" : iconColors[item.label]}
                />
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MobileTenantAdminGroup({
  pathname,
  navLinkClass,
  onNavigate,
  defaultExpanded = false,
  groups,
}) {
  const [expanded, setExpanded] = useState(
    defaultExpanded || isTenantAdminDropdownActive(pathname),
  );
  const [openSection, setOpenSection] = useState(() => {
    const current = groups.find((group) =>
      group.items.some((item) => isNavActive(item.path, pathname)),
    );
    return current?.label || null;
  });
  const active = isTenantAdminDropdownActive(pathname);

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        data-tour-id="nav-tenant-admins"
        className={`${navLinkClass(active)} w-full`}
        aria-expanded={expanded}
      >
        {active && (
          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        )}
        <Building2
          size={16}
          className={active ? "text-white" : iconColors["Tenant Admins"]}
        />
        <span className="flex-1 text-left">Tenant Admins</span>
        <ChevronDown
          size={14}
          className={`opacity-70 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="ml-2 space-y-1 border-l border-white/10 pl-2">
          {groups.map((group) => {
            const GroupIcon = group.icon;
            const sectionOpen = openSection === group.label;
            const sectionActive = group.items.some((item) =>
              isNavActive(item.path, pathname),
            );
            return (
              <div key={group.label} className="space-y-1">
                <button
                  type="button"
                  onClick={() =>
                    setOpenSection((prev) =>
                      prev === group.label ? null : group.label,
                    )
                  }
                  className={`${navLinkClass(sectionOpen || sectionActive)} w-full`}
                  aria-expanded={sectionOpen}
                >
                  {GroupIcon && (
                    <GroupIcon
                      size={15}
                      className={
                        sectionOpen || sectionActive
                          ? "text-white"
                          : "text-sky-200"
                      }
                    />
                  )}
                  <span className="flex-1 text-left">{group.label}</span>
                  <ChevronDown
                    size={14}
                    className={`opacity-70 transition-transform ${
                      sectionOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {sectionOpen && (
                  <div className="ml-2 space-y-1 border-l border-white/10 pl-2">
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;
                      const itemActive = isNavActive(item.path, pathname);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={onNavigate}
                          data-tour-id={navTourId(item.label)}
                          className={navLinkClass(itemActive)}
                        >
                          {itemActive && (
                            <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                          )}
                          <ItemIcon
                            size={15}
                            className={
                              itemActive ? "text-white" : iconColors[item.label]
                            }
                          />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ThemeShellBg = ({ variant = "top" }) => (
  <>
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(160deg, #0a1628 0%, #0d2145 40%, #0f2d6b 70%, #112366 100%)",
      }}
    />
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 20% 15%, rgba(99,102,241,0.55) 0%, transparent 55%), radial-gradient(ellipse 60% 70% at 80% 85%, rgba(14,165,233,0.42) 0%, transparent 55%)",
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }}
    />
    {variant === "top" ? (
      <>
        <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-indigo-500/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 right-0 w-36 h-36 rounded-full bg-sky-500/20 blur-3xl pointer-events-none" />
      </>
    ) : (
      <>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-indigo-500/25 blur-3xl pointer-events-none" />
        <div className="absolute -top-12 right-0 w-36 h-36 rounded-full bg-sky-500/20 blur-3xl pointer-events-none" />
      </>
    )}
  </>
);

function NotificationCard({ item, expanded, onToggleExpand, onDismiss }) {
  const { Icon, color, bg, ring } = getNotificationCategoryMeta(item.category);
  const { lead, body, byline } = parseNotificationMessage(item.message);
  const displayBody = body || item.message;
  const isLong = displayBody.length > 100;
  const showFull = expanded || !isLong;

  return (
    <div
      className={`group rounded-xl border transition-all ${
        item.is_read
          ? "border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm"
          : "border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white hover:border-indigo-200 hover:shadow-md"
      }`}
    >
      <div className="flex items-start gap-3 p-3">
        <div
          className={`w-9 h-9 rounded-xl ${bg} ring-1 ${ring} flex items-center justify-center flex-shrink-0`}
        >
          <Icon size={15} className={color} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="text-left flex-1 min-w-0">
              <span className="text-[12px] font-semibold leading-snug block text-slate-800">
                {item.title}
              </span>
            </div>
            {!item.is_read && (
              <span
                className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0 mt-1"
                title="Unread"
              />
            )}
          </div>

          {lead && (
            <p
              className="text-[10px] text-slate-500 mt-1 leading-snug line-clamp-2"
              title={lead}
            >
              {lead}
            </p>
          )}

          {displayBody && (
            <div className="mt-1.5">
              <p
                className={`text-[11px] text-slate-700 leading-relaxed ${showFull ? "whitespace-normal break-words" : "line-clamp-2"}`}
              >
                {displayBody}
              </p>
              {isLong && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(item.id);
                  }}
                  className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 mt-1"
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-slate-100/80">
            <div className="flex flex-col gap-0.5 min-w-0">
              {byline && (
                <span className="text-[9px] text-slate-500 truncate">
                  {byline}
                </span>
              )}
              <span className="text-[9px] text-slate-400">
                {formatRelativeTime(item.created_at)}
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => onDismiss(item.id, e)}
          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0 opacity-60 group-hover:opacity-100"
          title="Dismiss"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);

    if (diffSec < 60) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDays === 1) return "yesterday";
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
}

export default function DashboardLayout({ children }) {
  const {
    currentUser,
    logout,
    notificationSoundEnabled,
    setNotificationSoundEnabled,
    playNotificationSound,
    desktopNotificationEnabled,
    setDesktopNotificationEnabled,
  } = useApp();
  const allowedModules = currentUser?.modules || [];
  const visibleAssessmentItems = filterByModules(
    USER_ASSESSMENT_ITEMS,
    allowedModules,
  );
  const visibleOperationsItems = filterByModules(
    USER_OPERATIONS_ITEMS,
    allowedModules,
  );
  const visibleResourcesItems = filterByModules(
    USER_RESOURCES_ITEMS,
    allowedModules,
  );
  const visibleTenantAdminItems = filterByModules(
    TENANT_ADMIN_DROPDOWN_ITEMS,
    allowedModules,
  );
  const visibleTenantAdminGroups = TENANT_ADMIN_MENU_GROUPS.map((group) => ({
    ...group,
    items: filterByModules(group.items, allowedModules),
  })).filter((group) => group.items.length > 0);
  const userNavItems = [...USER_NAV];
  // if (currentUser?.can_manage_head_delegates) {
  //   userNavItems.splice(2, 0, {
  //     icon: Crown,
  //     label: "Head Department",
  //     path: "/user/head-department",
  //   });
  // }
  const visibleUserNav = filterByModules(userNavItems, allowedModules);
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [expandedNotificationId, setExpandedNotificationId] = useState(null);
  const [liveAlert, setLiveAlert] = useState(null);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const seenNotificationIdsRef = useRef(new Set());
  const notificationsPrimedRef = useRef(false);
  const lastUnreadCountRef = useRef(null);
  const bellOpenRef = useRef(false);
  const liveAlertTimerRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const lastAnnouncedAtRef = useRef(0);

  const rememberNotificationIds = useCallback((items) => {
    const incoming = items || [];
    const fresh = incoming.filter(
      (item) => item?.id && !seenNotificationIdsRef.current.has(item.id),
    );
    incoming.forEach((item) => {
      if (item?.id) seenNotificationIdsRef.current.add(item.id);
    });
    const isNew = notificationsPrimedRef.current && fresh.length > 0;
    notificationsPrimedRef.current = true;
    return isNew ? fresh : [];
  }, []);

  const announceInbox = useCallback(
    (freshItems) => {
      if (!freshItems?.length) return;
      const now = Date.now();
      if (now - lastAnnouncedAtRef.current < 1200) return;
      lastAnnouncedAtRef.current = now;

      const newest = freshItems[0];
      const title =
        freshItems.length > 1
          ? `${freshItems.length} new notifications`
          : newest?.title || "New notification";
      const parsed = parseNotificationMessage(newest?.message || "");
      const snippet = (
        freshItems.length > 1
          ? newest?.title || parsed.lead || parsed.body || newest?.message || ""
          : parsed.lead || parsed.body || newest?.message || ""
      ).slice(0, 160);

      playNotificationSound();
      if (typeof document !== "undefined" && document.hidden) {
        showDesktopNotification(title, snippet, {
          silent: !isNotificationSoundEnabled(),
        });
      }
      if (bellOpenRef.current) return;

      setLiveAlert({ title, snippet });
      if (liveAlertTimerRef.current) {
        window.clearTimeout(liveAlertTimerRef.current);
      }
      liveAlertTimerRef.current = window.setTimeout(
        () => setLiveAlert(null),
        7000,
      );
    },
    [playNotificationSound],
  );

  const fetchNotifications = useCallback(
    async (currentOffset = 0) => {
      try {
        if (currentOffset > 0) {
          setLoadingMore(true);
        }
        const res = await apiFetch(
          `/notifications?limit=10&offset=${currentOffset}`,
        );
        if (res.ok) {
          const data = await res.json();
          if (currentOffset === 0) {
            setNotifications(data.items || []);
            rememberNotificationIds(data.items || []);
          } else {
            setNotifications((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNew = (data.items || []).filter(
                (item) => !existingIds.has(item.id),
              );
              rememberNotificationIds(uniqueNew);
              return [...prev, ...uniqueNew];
            });
          }
          const unread = data.unread_count || 0;
          lastUnreadCountRef.current = unread;
          setUnreadCount(unread);
          setHasMore(data.has_more || false);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoadingMore(false);
      }
    },
    [rememberNotificationIds],
  );

  const pollNotifications = useCallback(async () => {
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const res = await apiFetch("/notifications?limit=10&offset=0");
      if (res.ok) {
        const data = await res.json();
        const items = data.items || [];
        const unread = data.unread_count || 0;
        const freshItems = rememberNotificationIds(items);
        lastUnreadCountRef.current = unread;
        setUnreadCount(unread);
        if (bellOpenRef.current) {
          setNotifications((prev) => {
            const incomingIds = new Set(items.map((item) => item.id));
            const rest = prev.filter((item) => !incomingIds.has(item.id));
            return [...items, ...rest];
          });
        } else {
          setNotifications(items);
        }
        setHasMore(Boolean(data.has_more));
        if (freshItems.length > 0) {
          announceInbox(freshItems);
        }
      }
    } catch (err) {
      console.error("Failed to poll notifications:", err);
    } finally {
      pollInFlightRef.current = false;
    }
  }, [announceInbox, rememberNotificationIds]);

  useEffect(() => {
    fetchNotifications(0);
    const interval = setInterval(pollNotifications, 3000);
    const onVisible = () => {
      if (!document.hidden) pollNotifications();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      if (liveAlertTimerRef.current) {
        window.clearTimeout(liveAlertTimerRef.current);
      }
    };
  }, [fetchNotifications, pollNotifications]);

  useEffect(() => {
    bellOpenRef.current = bellOpen;
    if (bellOpen) {
      setOffset(0);
      setLiveAlert(null);
      fetchNotifications(0);
    } else {
      setExpandedNotificationId(null);
    }
  }, [bellOpen, fetchNotifications]);

  useEffect(() => {
    if (!bellOpen) return undefined;
    const handleOutsideClick = (e) => {
      if (
        bellRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setBellOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [bellOpen]);

  // Profile state
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const profileDropdownRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return undefined;
    const handleOutsideClick = (e) => {
      if (
        profileRef.current?.contains(e.target) ||
        profileDropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setProfileOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [profileOpen]);

  const handleMarkAsRead = async (id) => {
    try {
      const res = await apiFetch(`/notifications/${id}/read`, {
        method: "PATCH",
      });
      if (res.ok) {
        setOffset(0);
        fetchNotifications(0);
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await apiFetch("/notifications/read-all", {
        method: "PATCH",
      });
      if (res.ok) {
        setOffset(0);
        fetchNotifications(0);
      }
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const res = await apiFetch(`/notifications/${id}`, { method: "DELETE" });
      if (res.ok) {
        setOffset(0);
        fetchNotifications(0);
      }
    } catch (err) {
      console.error("Failed to delete notification:", err);
    }
  };

  const handleToggleNotificationExpand = (id) => {
    setExpandedNotificationId((prev) => (prev === id ? null : id));
  };

  const handleClearAll = async () => {
    try {
      const res = await apiFetch("/notifications/clear-all", {
        method: "DELETE",
      });
      if (res.ok) {
        setOffset(0);
        fetchNotifications(0);
      }
    } catch (err) {
      console.error("Failed to clear notifications:", err);
    }
  };

  const handleScroll = (e) => {
    const container = e.target;
    if (
      container.scrollHeight - container.scrollTop <=
        container.clientHeight + 10 &&
      hasMore &&
      !loadingMore
    ) {
      const nextOffset = offset + 10;
      setOffset(nextOffset);
      fetchNotifications(nextOffset);
    }
  };

  const nav =
    currentUser?.role === "admin"
      ? ADMIN_NAV
      : currentUser?.role === "tenant_admin"
        ? TENANT_NAV
        : USER_NAV;

  const isUserRole = currentUser?.role === "user";

  const displayName = getDisplayName(currentUser);
  const departmentLabel = getDepartmentLabel(currentUser);
  const avatarLetter = displayName[0]?.toUpperCase() ?? "?";

  const FULLSCREEN_ROUTES = [
    "/user/pia",
    "/user/ropa",
    "/user/tprm",
    "/user/gap-assessment",
  ];
  const isFullscreen = FULLSCREEN_ROUTES.some(
    (r) => location.pathname === r || location.pathname.startsWith(`${r}/`),
  );

  const navLinkClass = (active, compact = false) =>
    `group relative flex flex-shrink-0 items-center gap-1.5 rounded-lg font-medium transition-all duration-200 overflow-hidden backdrop-blur-md border whitespace-nowrap ${
      compact ? "px-2 py-1 text-[11px] lg:text-xs" : "px-3 py-1 text-xs"
    } ${
      active
        ? "bg-white/10 text-white border-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.2)]"
        : "text-sky-100/90 border-transparent hover:bg-white/[0.07] hover:border-white/15 hover:text-white"
    }`;

  return (
    <div
      className={`flex flex-col bg-white ${isFullscreen ? "h-screen overflow-hidden" : "min-h-screen"}`}
    >
      <style>{`
      .brand-gold-text {
  background: linear-gradient(100deg, #8C7A55 0%, #C9B383 25%, #F1E9D8 50%, #C9B383 75%, #8C7A55 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
      @keyframes brandShine { to { background-position: 200% center; } }
      .nav-scroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .nav-scroll::-webkit-scrollbar {
        display: none;
      }
    `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 relative h-[52px] border-b border-white/10 shadow-md shrink-0">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <ThemeShellBg variant="top" />
        </div>

        <div className="relative z-10 h-full flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-5">
          {/* Left — menu + logo + nav */}
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-1 rounded-lg text-sky-100 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
            >
              <Menu size={17} />
            </button>

            <div className="flex items-center gap-1.5 pr-2 border-r border-white/10 flex-shrink-0">
              <img
                src="/OneDPDP_Icon.png"
                alt="OneDPDP"
                className="w-7 h-7 rounded-[8px] object-cover flex-shrink-0"
              />
              <span className="brand-gold-text font-semibold text-xs sm:text-sm tracking-tight hidden sm:inline">
                OneDPDP
              </span>
            </div>

            <nav className="hidden lg:flex items-center justify-start gap-1 min-w-0 pl-1.5 overflow-x-auto nav-scroll">
              {isUserRole ? (
                <>
                  {visibleUserNav.slice(0, 3).map((item) => (
                    <TopNavLink
                      key={item.path}
                      item={item}
                      active={isNavActive(item.path, location.pathname)}
                      navLinkClass={navLinkClass}
                      iconColors={iconColors}
                    />
                  ))}

                  {visibleAssessmentItems.length > 0 && (
                    <NavDropdown
                      label="Assessment"
                      icon={ClipboardCheck}
                      items={visibleAssessmentItems}
                      pathname={location.pathname}
                      navLinkClass={navLinkClass}
                      iconColor={iconColors.Assessment}
                    />
                  )}

                  {visibleOperationsItems.length > 0 && (
                    <NavDropdown
                      label="Operations"
                      icon={ShieldCheck}
                      items={visibleOperationsItems}
                      pathname={location.pathname}
                      navLinkClass={navLinkClass}
                      iconColor={iconColors.Operations}
                      isActiveCheck={isOperationsActive}
                    />
                  )}

                  {visibleResourcesItems.length > 0 && (
                    <NavDropdown
                      label="Resources"
                      icon={BookOpen}
                      items={visibleResourcesItems}
                      pathname={location.pathname}
                      navLinkClass={navLinkClass}
                      iconColor={iconColors.Resources}
                      isActiveCheck={isResourcesActive}
                    />
                  )}
                </>
              ) : currentUser?.role === "tenant_admin" ? (
                <>
                  {TENANT_NAV.slice(0, 1).map((item) => (
                    <TopNavLink
                      key={item.path}
                      item={item}
                      active={isNavActive(item.path, location.pathname)}
                      navLinkClass={navLinkClass}
                      iconColors={iconColors}
                    />
                  ))}

                  <NavDropdown
                    label="Tenant Admins"
                    icon={Building2}
                    items={visibleTenantAdminItems}
                    groups={visibleTenantAdminGroups}
                    pathname={location.pathname}
                    navLinkClass={navLinkClass}
                    iconColor={iconColors["Tenant Admins"]}
                    isActiveCheck={isTenantAdminDropdownActive}
                  />

                  {TENANT_NAV.slice(1).map((item) => (
                    <TopNavLink
                      key={item.path}
                      item={item}
                      active={isNavActive(item.path, location.pathname)}
                      navLinkClass={navLinkClass}
                      iconColors={iconColors}
                    />
                  ))}
                </>
              ) : (
                nav.map((item) => (
                  <TopNavLink
                    key={item.path}
                    item={item}
                    active={location.pathname === item.path}
                    navLinkClass={navLinkClass}
                    iconColors={iconColors}
                  />
                ))
              )}
            </nav>
          </div>

          {/* Right — actions */}
          <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() =>
                setNotificationSoundEnabled(!notificationSoundEnabled)
              }
              className="relative p-1 rounded-lg text-sky-200 hover:text-white hover:bg-white/10 transition-all focus:outline-none"
              title={
                notificationSoundEnabled
                  ? "Notification sound on"
                  : "Notification sound off"
              }
              aria-pressed={notificationSoundEnabled}
              aria-label={
                notificationSoundEnabled
                  ? "Turn notification sound off"
                  : "Turn notification sound on"
              }
            >
              {notificationSoundEnabled ? (
                <Volume2 size={15} />
              ) : (
                <VolumeX size={15} />
              )}
            </button>
            <button
              type="button"
              onClick={() =>
                setDesktopNotificationEnabled(!desktopNotificationEnabled)
              }
              className="relative p-1 rounded-lg text-sky-200 hover:text-white hover:bg-white/10 transition-all focus:outline-none"
              title={
                desktopNotificationEnabled
                  ? "Desktop notifications on"
                  : "Desktop notifications off"
              }
              aria-pressed={desktopNotificationEnabled}
              aria-label={
                desktopNotificationEnabled
                  ? "Turn desktop notifications off"
                  : "Turn desktop notifications on"
              }
            >
              {desktopNotificationEnabled ? (
                <Monitor size={15} />
              ) : (
                <MonitorOff size={15} />
              )}
            </button>
            <div className="relative" ref={bellRef}>
              <button
                onClick={() => setBellOpen(!bellOpen)}
                className="relative p-1 rounded-lg text-sky-200 hover:text-white hover:bg-white/10 transition-all focus:outline-none"
              >
                <Bell size={15} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 bg-rose-500 rounded-full text-[8px] font-bold text-white flex items-center justify-center border border-slate-900">
                    {unreadCount >= 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {liveAlert && !bellOpen && (
                <button
                  type="button"
                  onClick={() => {
                    setLiveAlert(null);
                    setBellOpen(true);
                  }}
                  className="absolute right-0 top-full mt-2 w-[min(320px,calc(100vw-1.5rem))] rounded-xl bg-white border border-indigo-100 shadow-[0_16px_40px_rgba(15,23,42,0.16)] p-3 text-left z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                      <Bell size={13} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-slate-800 leading-snug">
                        {liveAlert.title}
                      </p>
                      {liveAlert.snippet ? (
                        <p className="text-[10px] text-slate-500 mt-0.5 leading-snug line-clamp-2">
                          {liveAlert.snippet}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </button>
              )}

              {bellOpen && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 mt-2 w-[min(420px,calc(100vw-1.5rem))] rounded-2xl bg-white border border-slate-200/80 shadow-[0_16px_40px_rgba(15,23,42,0.12)] overflow-hidden text-left z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/30">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-sm">
                        <Bell size={14} className="text-white" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-slate-800 block leading-none">
                          Notifications
                        </span>
                        {unreadCount > 0 && (
                          <span className="text-[10px] text-indigo-600 font-medium">
                            {unreadCount} unread
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          Mark all read
                        </button>
                      )}
                      {notifications.length > 0 && (
                        <button
                          onClick={handleClearAll}
                          className="text-[10px] font-semibold text-slate-500 hover:text-red-500 transition-colors"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>

                  <div
                    onScroll={handleScroll}
                    className="max-h-[min(420px,70vh)] overflow-y-auto p-2 space-y-2 bg-slate-50/40"
                  >
                    {notifications.length === 0 ? (
                      <div className="py-10 px-4 flex flex-col items-center justify-center text-center bg-white rounded-xl border border-dashed border-slate-200">
                        <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                          <Bell size={18} className="text-slate-400" />
                        </div>
                        <p className="text-sm font-semibold text-slate-700">
                          All caught up!
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          No new notifications at this time.
                        </p>
                      </div>
                    ) : (
                      <>
                        {notifications.map((item) => (
                          <NotificationCard
                            key={item.id}
                            item={item}
                            expanded={expandedNotificationId === item.id}
                            onToggleExpand={handleToggleNotificationExpand}
                            onDismiss={handleDelete}
                          />
                        ))}
                        {loadingMore && (
                          <div className="py-2.5 text-center text-[10px] font-semibold text-slate-400">
                            Loading more...
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition-all focus:outline-none text-left"
              >
                <div className="w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-semibold text-[10px]">
                  {avatarLetter}
                </div>
                <span className="hidden sm:inline text-xs text-sky-200/90 font-medium max-w-[120px] truncate">
                  {currentUser?.role === "admin"
                    ? "Super Admin"
                    : currentUser?.role === "tenant_admin"
                      ? "Tenant Admin"
                      : displayName.split(" ")[0]}
                </span>
                <ChevronDown size={12} className="text-sky-300" />
              </button>

              {profileOpen && (
                <div
                  ref={profileDropdownRef}
                  className="absolute right-0 mt-2 w-64 rounded-xl bg-white border border-slate-100 shadow-[0_10px_30px_rgba(0,0,0,0.1)] overflow-hidden text-left z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  {/* User Profile Header */}
                  <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100">
                    <p className="text-xs font-bold text-slate-800 truncate">
                      {displayName}
                    </p>
                    <p className="text-[10px] text-slate-500 truncate mt-0.5">
                      {currentUser?.email}
                    </p>
                    {departmentLabel && (
                      <p
                        className="text-[10px] text-slate-600 mt-1.5 flex items-center gap-1 min-w-0"
                        title={departmentLabel}
                      >
                        <Building2
                          size={10}
                          className="text-indigo-500 shrink-0"
                        />
                        <span className="truncate">{departmentLabel}</span>
                      </p>
                    )}
                    <div className="mt-2">
                      <span className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[9px] font-bold text-indigo-600 capitalize">
                        {currentUser?.role?.replace("_", " ")}
                      </span>
                    </div>
                  </div>

                  {/* Actions List */}
                  <div className="p-1.5 bg-white">
                    <button
                      type="button"
                      onClick={() =>
                        setNotificationSoundEnabled(!notificationSoundEnabled)
                      }
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all focus:outline-none"
                    >
                      <span className="flex items-center gap-2">
                        {notificationSoundEnabled ? (
                          <Volume2
                            size={13}
                            className="flex-shrink-0 text-indigo-600"
                          />
                        ) : (
                          <VolumeX
                            size={13}
                            className="flex-shrink-0 text-slate-400"
                          />
                        )}
                        <span>Notification sound</span>
                      </span>
                      <span
                        className={`inline-flex items-center justify-center w-9 h-5 rounded-full transition-colors relative ${
                          notificationSoundEnabled
                            ? "bg-indigo-600"
                            : "bg-slate-200"
                        }`}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded-full bg-white transition-transform absolute shadow-sm ${
                            notificationSoundEnabled
                              ? "translate-x-1.5"
                              : "-translate-x-1.5"
                          }`}
                        />
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setDesktopNotificationEnabled(
                          !desktopNotificationEnabled,
                        )
                      }
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all focus:outline-none"
                    >
                      <span className="flex items-center gap-2">
                        {desktopNotificationEnabled ? (
                          <Monitor
                            size={13}
                            className="flex-shrink-0 text-indigo-600"
                          />
                        ) : (
                          <MonitorOff
                            size={13}
                            className="flex-shrink-0 text-slate-400"
                          />
                        )}
                        <span>Desktop notifications</span>
                      </span>
                      <span
                        className={`inline-flex items-center justify-center w-9 h-5 rounded-full transition-colors relative ${
                          desktopNotificationEnabled
                            ? "bg-indigo-600"
                            : "bg-slate-200"
                        }`}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded-full bg-white transition-transform absolute shadow-sm ${
                            desktopNotificationEnabled
                              ? "translate-x-1.5"
                              : "-translate-x-1.5"
                          }`}
                        />
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        setProfileOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-500 hover:text-red-600 hover:bg-slate-50 rounded-lg transition-all focus:outline-none"
                    >
                      <LogOut size={13} className="flex-shrink-0" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── MOBILE DRAWER ──────────────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative w-64 overflow-hidden p-4 space-y-2 shadow-2xl">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(160deg, #0a1628 0%, #0d2145 40%, #0f2d6b 70%, #112366 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 80% 60% at 20% 15%, rgba(99,102,241,0.55) 0%, transparent 55%), radial-gradient(ellipse 60% 70% at 80% 85%, rgba(14,165,233,0.42) 0%, transparent 55%)",
              }}
            />
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <span className="text-white font-semibold block">
                    {displayName}
                  </span>
                  <span className="text-sky-300 text-xs">
                    {currentUser?.role === "admin"
                      ? "Super Admin"
                      : currentUser?.role === "tenant_admin"
                        ? "Tenant Admin"
                        : "User"}
                  </span>
                  {departmentLabel && (
                    <span className="text-sky-200/80 text-[10px] block mt-0.5 truncate max-w-[160px]">
                      {departmentLabel}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1.5 rounded-lg text-white hover:bg-white/10"
                >
                  <X className="text-white" />
                </button>
              </div>

              {isUserRole ? (
                <>
                  {visibleUserNav.slice(0, 3).map((item) => {
                    const Icon = item.icon;
                    const active = isNavActive(item.path, location.pathname);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-tour-id={navTourId(item.label)}
                        className={navLinkClass(active)}
                      >
                        {active && (
                          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                        )}
                        <Icon
                          size={16}
                          className={
                            active ? "text-white" : iconColors[item.label]
                          }
                        />
                        {item.label}
                      </Link>
                    );
                  })}

                  {visibleAssessmentItems.length > 0 && (
                    <MobileNavGroup
                      label="Assessment"
                      icon={ClipboardCheck}
                      iconColor={iconColors.Assessment}
                      tourId="nav-assessment"
                      pathname={location.pathname}
                      navLinkClass={navLinkClass}
                      onNavigate={() => setMobileOpen(false)}
                      defaultExpanded={isAssessmentActive(location.pathname)}
                      items={visibleAssessmentItems}
                      isActiveCheck={isAssessmentActive}
                    />
                  )}

                  {visibleOperationsItems.length > 0 && (
                    <MobileNavGroup
                      label="Operations"
                      icon={ShieldCheck}
                      iconColor={iconColors.Operations}
                      tourId="nav-operations"
                      pathname={location.pathname}
                      navLinkClass={navLinkClass}
                      onNavigate={() => setMobileOpen(false)}
                      defaultExpanded={isOperationsActive(location.pathname)}
                      items={visibleOperationsItems}
                      isActiveCheck={isOperationsActive}
                    />
                  )}

                  {visibleResourcesItems.length > 0 && (
                    <MobileNavGroup
                      label="Resources"
                      icon={BookOpen}
                      iconColor={iconColors.Resources}
                      tourId="nav-resources"
                      pathname={location.pathname}
                      navLinkClass={navLinkClass}
                      onNavigate={() => setMobileOpen(false)}
                      defaultExpanded={isResourcesActive(location.pathname)}
                      items={visibleResourcesItems}
                      isActiveCheck={isResourcesActive}
                    />
                  )}
                </>
              ) : currentUser?.role === "tenant_admin" ? (
                <>
                  {/* Dashboard */}
                  {TENANT_NAV.slice(0, 1).map((item) => {
                    const Icon = item.icon;
                    const active = isNavActive(item.path, location.pathname);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-tour-id={navTourId(item.label)}
                        className={navLinkClass(active)}
                      >
                        {active && (
                          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                        )}
                        <Icon
                          size={16}
                          className={
                            active ? "text-white" : iconColors[item.label]
                          }
                        />
                        {item.label}
                      </Link>
                    );
                  })}

                  <MobileTenantAdminGroup
                    pathname={location.pathname}
                    navLinkClass={navLinkClass}
                    onNavigate={() => setMobileOpen(false)}
                    defaultExpanded={isTenantAdminDropdownActive(
                      location.pathname,
                    )}
                    groups={visibleTenantAdminGroups}
                  />

                  {/* License, Settings, Help */}
                  {TENANT_NAV.slice(1).map((item) => {
                    const Icon = item.icon;
                    const active = isNavActive(item.path, location.pathname);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-tour-id={navTourId(item.label)}
                        className={navLinkClass(active)}
                      >
                        {active && (
                          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                        )}
                        <Icon
                          size={16}
                          className={
                            active ? "text-white" : iconColors[item.label]
                          }
                        />
                        {item.label}
                      </Link>
                    );
                  })}
                </>
              ) : (
                nav.map((item) => {
                  const Icon = item.icon;
                  const active = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      data-tour-id={navTourId(item.label)}
                      className={navLinkClass(active)}
                    >
                      {active && (
                        <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      )}
                      <Icon
                        size={16}
                        className={
                          active ? "text-white" : iconColors[item.label]
                        }
                      />
                      {item.label}
                    </Link>
                  );
                })
              )}

              <button
                type="button"
                onClick={() =>
                  setNotificationSoundEnabled(!notificationSoundEnabled)
                }
                className="mt-4 w-full text-left px-3 py-2 rounded-xl text-sky-100 hover:bg-white/10 border border-transparent hover:border-white/10 flex items-center justify-between gap-2 backdrop-blur-md transition-all"
              >
                <span className="flex items-center gap-2">
                  {notificationSoundEnabled ? (
                    <Volume2 size={16} />
                  ) : (
                    <VolumeX size={16} />
                  )}
                  Notification sound
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-300">
                  {notificationSoundEnabled ? "On" : "Off"}
                </span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setDesktopNotificationEnabled(!desktopNotificationEnabled)
                }
                className="mt-2 w-full text-left px-3 py-2 rounded-xl text-sky-100 hover:bg-white/10 border border-transparent hover:border-white/10 flex items-center justify-between gap-2 backdrop-blur-md transition-all"
              >
                <span className="flex items-center gap-2">
                  {desktopNotificationEnabled ? (
                    <Monitor size={16} />
                  ) : (
                    <MonitorOff size={16} />
                  )}
                  Desktop notifications
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-sky-300">
                  {desktopNotificationEnabled ? "On" : "Off"}
                </span>
              </button>
              <button
                onClick={logout}
                className="mt-2 w-full text-left px-3 py-2 rounded-xl text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-400/20 flex items-center gap-2 backdrop-blur-md transition-all"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT + FOOTER ────────────────────────────────────────────── */}
      <main
        className={`flex-1 flex flex-col text-slate-800 ${
          isFullscreen ? "min-h-0 overflow-hidden" : ""
        }`}
      >
        <div
          className={`${
            isFullscreen
              ? "flex-1 min-h-0 flex flex-col overflow-y-auto"
              : "flex-1"
          } ${isFullscreen ? "" : "p-4 lg:p-6 pb-0"}`}
        >
          <div className={isFullscreen ? "min-h-full" : "pb-4"}>{children}</div>
        </div>

        <footer className="relative h-10 shrink-0 overflow-hidden border-t border-white/10 shadow-md px-3 sm:px-4 lg:px-5">
          <ThemeShellBg variant="bottom" />
          <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none z-10" />
          <div className="relative z-10 h-full flex items-center justify-between">
            <span className="text-[11px] leading-none text-sky-200/90 font-medium">
              &copy; 2026 Whitehats Technologies
            </span>
            <span className="text-[11px] leading-none text-sky-200/90 font-medium">
              v1.0.0
            </span>
          </div>
        </footer>
      </main>
      <LiveTourOverlay />
    </div>
  );
}
